import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Loader2, MapPin, Search } from "lucide-react";

/**
 * UK-wide location autocomplete backed by OpenStreetMap Nominatim (free, no key).
 * - Searches UK only (countrycodes=gb).
 * - Debounced 400ms, min 3 chars.
 * - Returns: { label, lat, lng, address } via onChange.
 *
 * Props:
 *   value         { label, lat, lng } | null         — currently selected location
 *   onChange      (loc) => void                      — fires when user picks a result
 *   placeholder   string                             — input placeholder
 *   testId        string                             — data-testid prefix for input + items
 */
export default function UKLocationAutocomplete({ value, onChange, placeholder, testId }) {
  const [query, setQuery] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  // Keep input in sync when parent updates value externally
  useEffect(() => {
    if (value?.label && value.label !== query) setQuery(value.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.label]);

  // Click-outside to close suggestions
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      return;
    }
    // Don't re-search if the input still exactly matches the selected value
    if (value && q === value.label) return;

    const t = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        const url =
          "https://nominatim.openstreetmap.org/search" +
          `?format=jsonv2&addressdetails=1&limit=6&countrycodes=gb&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, {
          signal: abortRef.current.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error("geocode failed");
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlight(-1);
      } catch (err) {
        if (err.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(t);
  }, [query, value]);

  const pick = (r) => {
    const label = pickLabel(r);
    const loc = {
      label,
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      address: r.display_name,
    };
    setQuery(label);
    setResults([]);
    setOpen(false);
    onChange?.(loc);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && results[highlight]) {
        e.preventDefault();
        pick(results[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6965]" />
        <Input
          data-testid={testId || "uk-location-input"}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "UK city, town, street or postcode"}
          autoComplete="off"
          className="rounded-xl pl-9 pr-9"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6965] animate-spin" />
        )}
      </div>

      {open && results.length > 0 && (
        <div
          data-testid={`${testId || "uk-location"}-results`}
          className="absolute z-30 mt-1 w-full bg-white border border-[#E2DFD8] rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {results.map((r, i) => (
            <button
              type="button"
              key={r.place_id}
              data-testid={`${testId || "uk-location"}-result-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(r)}
              className={`w-full text-left px-3 py-2 flex gap-2 items-start transition-colors ${
                highlight === i ? "bg-[#F1EDE5]" : "bg-white hover:bg-[#F8F5EE]"
              }`}
            >
              <MapPin className="w-4 h-4 text-[#1B3624] mt-1 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1B3624] truncate">{pickLabel(r)}</div>
                <div className="text-xs text-[#6B6965] truncate">{r.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && query.trim().length >= 3 && results.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-[#E2DFD8] rounded-xl shadow-lg px-3 py-2 text-sm text-[#6B6965]">
          No UK locations match "{query}". Try a postcode or town name.
        </div>
      )}
    </div>
  );
}

// Build a short, human label from Nominatim's addressdetails — prefer postcode + town
function pickLabel(r) {
  const a = r.address || {};
  const town = a.city || a.town || a.village || a.hamlet || a.suburb || a.county;
  const postcode = a.postcode;
  if (postcode && town) return `${postcode} · ${town}`;
  if (postcode) return postcode;
  if (town) return town;
  // Fallback: first chunk of display_name
  return (r.display_name || "").split(",").slice(0, 2).join(", ").trim();
}
